import { plotosMode, active_project } from './router.js';
import { initScene } from './scene-setup.js';
import { loadAssets } from './asset-loader.js';
import { flyTo, initCameraControls } from './camera-controls.js';
import { initUI } from './ui-logic.js';
import { initNearbyLocations } from './nearby-locations.js';
import { initRenderLoop, checkDeepLink } from './render-loop.js';
import { fetchData, startPolling } from './data-sync.js';
import { initAudioHdri } from './audio-hdri.js';
import { initGallery } from './gallery.js';
import { trackEvent } from './supabase-client.js';

document.addEventListener('DOMContentLoaded', async () => {
   'use strict';

   /* ═══════════════════════════════════════════
      § 0. SUBDOMAIN ROUTER
      ═══════════════════════════════════════════ */
   if (plotosMode !== 'CLIENT_MAP') {
      document.getElementById('plotos-idle').classList.add('active');
      console.log(`[PlotOs Router] Mode: ${plotosMode} — Engine idle.`);
      return; // Stop — do not boot 3D engine
   }
   console.log(`[PlotOs Router] Mode: ${plotosMode} | Project: ${active_project}`);

   /* ═══════════════════════════════════════════
      § 1 + § 2. SCENE + LIGHTING
      ═══════════════════════════════════════════ */
   const ctx = initScene();

   /* ═══════════════════════════════════════════
      § 4. GLB ASSET LOAD + GPU WARM-UP
      ═══════════════════════════════════════════ */
   try {
      await loadAssets(active_project, ctx);
   } catch (e) {
      return; // loadAssets shows "ASSET LOAD FAILED" in the loader UI
   }

   /* ═══════════════════════════════════════════
      § 8. UI — wired before controls so callbacks exist
      ═══════════════════════════════════════════ */
   const { renderCard, selectPlot, deselectAll, toggleMap } = initUI(ctx, { flyTo });

   /* ═══════════════════════════════════════════
      § 7. RAYCASTER + CLICK HANDLING
      ═══════════════════════════════════════════ */
   initCameraControls(ctx, { selectPlot, deselectAll });

   /* ═══════════════════════════════════════════
      § 9a. NEARBY LOCATIONS
      ═══════════════════════════════════════════ */
   const { loadLocations } = initNearbyLocations(active_project, ctx, { toggleMap, deselectAll });

   /* ═══════════════════════════════════════════
      § 9b. RENDER LOOP (starts animate())
      ═══════════════════════════════════════════ */
   initRenderLoop(ctx);

   /* ═══════════════════════════════════════════
      § 6. FIRST DATA PULL
      fetchData(init=true) calls loadLocations()
      internally when the sync indicator turns LIVE.
      ═══════════════════════════════════════════ */
   await fetchData(active_project, true, { renderCard, loadLocations });

   /* ═══════════════════════════════════════════
      DEEP-LINK: ?plot= query param
      Must run after fetchData so crmData is ready.
      ═══════════════════════════════════════════ */
   checkDeepLink(ctx, { selectPlot, flyTo });

   /* ═══════════════════════════════════════════
      CONDITIONAL ASSETS (fire-and-forget)
      HDRI sky + BGM/SFX + Page Visibility API
      ═══════════════════════════════════════════ */
   initAudioHdri(ctx);

   /* ═══════════════════════════════════════════
      § 6. POLLING — 10s live sync interval
      ═══════════════════════════════════════════ */
   startPolling(active_project, { renderCard, loadLocations });

   /* ═══════════════════════════════════════════
      § 10. PROJECT GALLERY
      ═══════════════════════════════════════════ */
   initGallery(active_project);

   /* ═══════════════════════════════════════════
      § 11. ANALYTICS — canvas_load
      Fired once: all systems live, data synced,
      gallery loaded. Fire-and-forget (no await).
      ═══════════════════════════════════════════ */
   trackEvent('canvas_load');
});
